import { FunctionField, NumberField, ReferenceField, SelectField, SimpleShowLayout, TextField, useTranslate } from 'react-admin';
import { useMediaQuery, useTheme } from '@mui/material';
import { DetailsSampleList } from '@/components/global/fields/DetailsSampleList.jsx';
import { ServiceCompanyNamesField } from '@/components/global/fields/ServiceCompanyNamesField.jsx';
import { RetailChainLogoField } from '@/components/retailChain/fields/RetailChainLogoField.jsx';
import { depositWithdrawalMethodsChoices, forwarderTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { operatorChoices } from '@/constants/Operators.js';
import { formatOptionalIntegerInput, formatOptionalNumberInput } from '@/components/retailChain/validators/retailChainValidators.js';

const DISCOUNT_LEVELS = [1, 2, 3, 4, 5, 6];

const couponSourceChoices = [
    { id: 'comp', name: 'Comp' },
    { id: 'zwrotka', name: 'Zwrotka' },
    { id: 'eurocash', name: 'Eurocash' },
    { id: 'maasloop', name: 'MaasLoop' },
    { id: 'kaucja', name: 'Kaucja' },
];

const choiceName = (choices, id) => choices.find(choice => choice.id === id)?.name || id;

const joinDiscountValues = (record, sourcePrefix, formatValue) =>
    DISCOUNT_LEVELS.map(level => {
        const formatted = formatOptionalIntegerInput(record?.[`${sourcePrefix}${level}`]);
        return formatted === '' ? '-' : formatValue(formatted);
    }).join(' · ');

const joinChoiceNames = (values, choices) => {
    const ids = (Array.isArray(values) ? values : []).map(value => (value && typeof value === 'object' ? value.id : value)).filter(Boolean);
    if (ids.length === 0) {
        return '';
    }

    return ids.map(id => choiceName(choices, id)).join(', ');
};

export const RetailChainBasicDetails = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <SimpleShowLayout className={isDesktop ? 'two-columns-show-layout' : ''}>
            <SimpleShowLayout className='no-label image' sx={{ marginBottom: isDesktop ? '0' : '32px', maxWidth: 'max-content' }}>
                <FunctionField source='logo' label={translate('resources.retailChain.fields.logo')} render={() => <RetailChainLogoField column />} />
            </SimpleShowLayout>
            <DetailsSampleList>
                <TextField source='name' label={translate('resources.retailChain.fields.name')} emptyText='—' />
                <TextField source='description' label={translate('resources.retailChain.fields.description')} emptyText='—' />
                <FunctionField source='brands' label={translate('resources.retailChain.fields.brands')} render={() => null} />
                <FunctionField source='contractors' label={translate('resources.retailChain.fields.contractors')} render={() => null} />
                <FunctionField source='collectionPoints' label={translate('resources.retailChain.fields.collectionPoints')} render={() => null} />
            </DetailsSampleList>
        </SimpleShowLayout>
    );
};

RetailChainBasicDetails.propTypes = {};

export const RetailChainConfigurationDetails = () => {
    const translate = useTranslate();

    return (
        <DetailsSampleList>
            <SelectField
                source='forwarderType'
                label={translate('resources.retailChain.fields.forwarderType')}
                choices={forwarderTypeChoices(translate)}
                emptyText='—'
            />
            <FunctionField
                source='operators'
                label={translate('resources.retailChain.fields.operators')}
                render={record => joinChoiceNames(record?.operators, operatorChoices) || '—'}
            />
            <SelectField source='couponSource' label={translate('resources.retailChain.fields.couponSource')} choices={couponSourceChoices} emptyText='—' />
            <FunctionField
                source='depositWithdrawalMethods'
                label={translate('resources.retailChain.fields.depositWithdrawalMethods')}
                render={record => joinChoiceNames(record?.depositWithdrawalMethods, depositWithdrawalMethodsChoices(translate)) || '—'}
            />
            <FunctionField
                source='defaultDepositValue'
                label={translate('resources.retailChain.fields.defaultDepositValue')}
                render={record => formatOptionalNumberInput(record?.defaultDepositValue) || '—'}
            />
            <NumberField source='defaultDepositPoints' label={translate('resources.retailChain.fields.defaultDepositPoints')} emptyText='—' />
            <ServiceCompanyNamesField source='serviceCompanyIds' label={translate('resources.retailChain.fields.serviceCompanies')} />
            <ReferenceField
                source='defaultServiceCompanyId'
                reference='service-company'
                link={false}
                emptyText='—'
                label={translate('resources.retailChain.fields.defaultServiceCompany')}
            >
                <TextField source='name' />
            </ReferenceField>
            <FunctionField
                source='discountLimitL1'
                label={translate('resources.retailChain.labels.discountLimits')}
                render={record => joinDiscountValues(record, 'discountLimitL', value => value)}
            />
            <FunctionField
                source='discountValueL1'
                label={translate('resources.retailChain.labels.discountValues')}
                render={record => joinDiscountValues(record, 'discountValueL', value => `${value}%`)}
            />
        </DetailsSampleList>
    );
};

RetailChainConfigurationDetails.propTypes = {};
